/**
 * Die Karte „Anlage" aus `ui_kits/dashboard/StromErzeugungScreen.jsx` — neben
 * „Verwendung": wie stark die Module gerade arbeiten und wie voll der Speicher
 * ist. **Sie heißt „Zustand der Erzeugungsanlagen"** (0.54.1): „Anlage" ist in
 * diesem Produkt das Haus, und neben der Kennzahlen-Karte „Erzeugungsanlagen"
 * stünde ein Wort für zweierlei.
 * Übernommen wie im Entwurf (HM-138): Unterzeile „Erzeugung · 8,4 kWp",
 * ein Balken „Auslastung" in der Farbe der Erzeugung, einer „Speicher ·
 * Ladezustand" bzw. „Speicher · lädt mit … kW" in der Datenpunktfarbe — derselben
 * wie die Linie des Ladezustands im Diagramm darüber.
 *
 * **Aus den Geräten der Erzeugungsanlagen, nicht aus dem Messkonzept**, wie die
 * Detailseite einer Anlage (HM-131): Die Auslastung ist die PV-Leistung je
 * installiertem kWp, der Ladezustand der des Speichers — beides kennt kein
 * Zähler. Gerechnet wird in der API (`GET /generation-assets/{id}/state`).
 *
 * **Je Anlage ein Balken**, mit ihrem Namen, sobald es mehr als eine gibt: Zwei
 * Dachflächen haben zwei Auslastungen, und ein Mittel daraus wäre die Auslastung
 * keiner von beiden. Der Ladezustand mehrerer Batterien in **einer** Anlage wird
 * ebenso wenig gemittelt — die API nennt ihn dann nicht. Und ein Balken ohne
 * frischen Wert ist ein Satz, kein leerer Balken: Der sähe aus wie 0 %.
 */
import { Card, ProgressBar, quantityColorVar } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { formatPowerFull } from "../lib/energy-format.js";
import { BATTERY_IDLE_W } from "../lib/generation-split.js";
import { LIVE_MAX_AGE_SECONDS } from "../lib/live-now.js";
import { useGenerationAssetState } from "../lib/queries.js";

type Asset = ApiComponents["schemas"]["GenerationAsset"];

const kwp = (value: number) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(value);
const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;

/** Die Anlagen, für die die Karte etwas zeigen kann: mit Gerät, PV zuerst. */
export function plantAssets(assets: readonly Asset[]): Asset[] {
  return assets
    .filter((asset) => asset.deviceIds.length > 0)
    .sort((a, b) => (a.kind === b.kind ? a.sortOrder - b.sortOrder : a.kind === "pv" ? -1 : 1));
}

export function PlantCard({
  assets,
  yieldNote,
}: {
  assets: readonly Asset[];
  /** Der Ertrag je kWp im Zeitraum (HM-122) — `null`, wo er sich nicht bilden lässt. */
  yieldNote: string | null;
}) {
  const peak = assets
    .filter((asset) => asset.kind === "pv" && asset.peakPowerKwp !== null)
    .reduce((sum, asset) => sum + asset.peakPowerKwp!, 0);
  const named = assets.length > 1;
  const subtitle = [peak > 0 ? `Erzeugung · ${kwp(peak)} kWp` : "Erzeugung", yieldNote]
    .filter((part): part is string => part !== null)
    .join(" · ");
  return (
    <Card title="Zustand der Erzeugungsanlagen" subtitle={subtitle}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 4 }}>
        {assets.map((asset) => (
          <AssetBar key={asset.id} asset={asset} named={named} />
        ))}
      </div>
    </Card>
  );
}

function AssetBar({ asset, named }: { asset: Asset; named: boolean }) {
  const state = useGenerationAssetState(asset.id).data;
  if (state === undefined) return null;
  const suffix = named ? ` · ${asset.label}` : "";
  /* Dieselbe Frist wie für jeden Momentanwert: Ein älterer Wert stünde als „jetzt" da. */
  const fresh =
    state.measuredAt !== null &&
    Date.now() - new Date(state.measuredAt).getTime() <= LIVE_MAX_AGE_SECONDS * 1000;

  if (asset.kind === "pv") {
    const utilisation = fresh ? (state.pv?.utilisation ?? null) : null;
    if (utilisation === null) return <Gap label={`Auslastung${suffix}`} />;
    return (
      <ProgressBar
        label={`Auslastung${suffix}`}
        value={utilisation * 100}
        valueLabel={percent(utilisation)}
        color={quantityColorVar("generation")}
      />
    );
  }

  const soc = fresh ? (state.battery?.soc ?? null) : null;
  if (soc === null) return <Gap label={`Speicher${suffix}`} />;
  const power = state.battery?.powerW ?? null;
  const charging = power !== null && power >= BATTERY_IDLE_W;
  return (
    <ProgressBar
      label={`Speicher${suffix} · ${charging ? `lädt mit ${formatPowerFull(power)}` : "Ladezustand"}`}
      value={soc * 100}
      valueLabel={percent(soc)}
      color="var(--hm-series-datapoint)"
    />
  );
}

function Gap({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 }}>
      <span style={{ color: "var(--text-body)" }}>{label}</span>
      <span style={{ color: "var(--text-muted)" }}>meldet gerade nichts</span>
    </div>
  );
}
